import React, { useState, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Building2,
  Flag,
  CheckCircle,
  Clock,
  AlertTriangle,
  Mail,
  List,
  RefreshCw,
  BarChart2,
} from 'lucide-react';
import { api } from '../services/api';
import { useSettings } from '../contexts/SettingsContext';
import AgencyLogo from '../components/AgencyLogo';
import type { KPIData } from '../types';

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  sub?: string;
  accent?: string;
  onClick?: () => void;
}

const StatCard: React.FC<StatCardProps> = ({ icon, label, value, sub, accent, onClick }) => {
  const [hovered, setHovered] = React.useState(false);
  const clickable = !!onClick;
  return (
    <div
      className="card stat"
      onClick={onClick}
      onMouseEnter={() => clickable && setHovered(true)}
      onMouseLeave={() => clickable && setHovered(false)}
      style={{
        borderTop: accent ? `3px solid ${accent}` : undefined,
        cursor: clickable ? 'pointer' : undefined,
        background: clickable && hovered ? 'var(--gray-50)' : undefined,
        transition: 'background 0.15s',
        position: 'relative',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <span style={{ color: accent ?? 'var(--blue)', opacity: 0.8 }}>{icon}</span>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {label}
        </span>
      </div>
      <div style={{ fontSize: 32, fontWeight: 700, color: 'var(--gray-900)', lineHeight: 1 }}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
      {sub && <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 6 }}>{sub}</div>}
      {clickable && (
        <span style={{
          position: 'absolute', bottom: 10, right: 12,
          fontSize: 16, color: accent ?? 'var(--blue)', opacity: hovered ? 0.8 : 0.3,
          transition: 'opacity 0.15s',
        }}>→</span>
      )}
    </div>
  );
};

const CoverageTile: React.FC<{ label: string; value: number; border: string; onClick: () => void }> = ({ label, value, border, onClick }) => {
  const [hovered, setHovered] = React.useState(false);
  return (
    <div
      className="card stat"
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        borderTop: `3px solid ${border}`,
        cursor: 'pointer',
        background: hovered ? 'var(--gray-50)' : undefined,
        transition: 'background 0.15s',
        position: 'relative',
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
        {label}
      </div>
      <div style={{ fontSize: 32, fontWeight: 700, color: 'var(--gray-900)', lineHeight: 1 }}>
        {value}
      </div>
      <span style={{
        position: 'absolute', bottom: 10, right: 12,
        fontSize: 16, color: border, opacity: hovered ? 0.8 : 0.3,
        transition: 'opacity 0.15s',
      }}>→</span>
    </div>
  );
};

const KPIDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { agingDays, overdueDays } = useSettings();
  const [showAllAgencies, setShowAllAgencies] = useState(false);
  const gapTableRef = useRef<HTMLDivElement>(null);
  const { data: kpi, isLoading, error, refetch } = useQuery<KPIData>({
    queryKey: ['kpi'],
    queryFn: () => api.getKPI(),
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading)
    return <div style={{ padding: 32, textAlign: 'center', color: 'var(--gray-500)' }}>Loading KPI data…</div>;

  if (error || !kpi)
    return <div style={{ padding: 32, textAlign: 'center', color: 'var(--red)' }}>Failed to load KPI data.</div>;

  const verifiedPct      = kpi.freshness.verified_pct  ?? 0;
  const agingPct         = kpi.freshness.aging_pct     ?? 0;
  const overduePct       = kpi.freshness.overdue_pct   ?? 0;
  const neverVerifiedPct = kpi.freshness.never_verified_pct ?? 0;

  return (
    <div style={{ paddingBottom: 40 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 900 }}>KPI Dashboard</h2>
        <button className="btn btn-secondary" onClick={() => refetch()}>
          <RefreshCw size={13} />
          Refresh
        </button>
      </div>

      {/* Contacts */}
      <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        Contacts
      </h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12, marginBottom: 24 }}>
        <StatCard
          icon={<Users size={18} />}
          label="Total Records"
          value={kpi.records.total}
          sub={`${kpi.records.active} active · ${kpi.records.archived} archived`}
          accent="var(--blue)"
          onClick={() => navigate('/directory')}
        />
        <StatCard
          icon={<CheckCircle size={18} />}
          label={`Verified (≤${agingDays}d)`}
          value={`${verifiedPct.toFixed(1)}%`}
          sub={`${kpi.freshness.verified.toLocaleString()} contacts`}
          accent="var(--green)"
          onClick={() => navigate('/directory?verification=verified')}
        />
        <StatCard
          icon={<Clock size={18} />}
          label={`Aging (${agingDays}–${overdueDays}d)`}
          value={`${agingPct.toFixed(1)}%`}
          sub={`${kpi.freshness.aging.toLocaleString()} contacts`}
          accent="var(--amber)"
          onClick={() => navigate('/directory?verification=aging')}
        />
        <StatCard
          icon={<AlertTriangle size={18} />}
          label={`Overdue (${overdueDays}d+)`}
          value={`${overduePct.toFixed(1)}%`}
          sub={`${kpi.freshness.overdue.toLocaleString()} contacts`}
          accent="var(--hazard-deep)"
          onClick={() => navigate('/directory?verification=overdue')}
        />
        <StatCard
          icon={<AlertTriangle size={18} />}
          label="Never Verified"
          value={kpi.freshness.never_verified.toLocaleString()}
          sub={`${neverVerifiedPct.toFixed(1)}% of total`}
          accent="var(--red)"
          onClick={() => navigate('/directory?verification=never')}
        />
      </div>

      {/* Flags & Requests */}
      <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        Quality & Workflow
      </h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12, marginBottom: 24 }}>
        <StatCard
          icon={<Flag size={18} />}
          label="Raised Flags"
          value={kpi.flags.open}
          sub={`${kpi.flags.resolved_30d} resolved last 30d`}
          accent="var(--red)"
        />
        <StatCard
          icon={<RefreshCw size={18} />}
          label="Update Requests"
          value={kpi.update_requests.pending}
          sub={`${kpi.update_requests.submitted} submitted · ${kpi.update_requests.expired} expired`}
          accent="#8b5cf6"
        />
        <StatCard
          icon={<List size={18} />}
          label="Active Lists"
          value={kpi.lists.active}
          sub={`${kpi.lists.total} total`}
          accent="var(--blue)"
        />
        <StatCard
          icon={<Mail size={18} />}
          label="Emails (30d)"
          value={kpi.communications.sent_30d}
          sub={kpi.communications.failed_30d > 0 ? `${kpi.communications.failed_30d} failed` : 'No failures'}
          accent="var(--green)"
        />
      </div>

      {/* System */}
      <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        System
      </h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12, marginBottom: 24 }}>
        <StatCard
          icon={<Building2 size={18} />}
          label="Agencies"
          value={kpi.system.agencies}
          accent="var(--blue)"
        />
        <StatCard
          icon={<Users size={18} />}
          label="System Roles"
          value={kpi.system.roles}
          accent="var(--blue)"
        />
        <StatCard
          icon={<Users size={18} />}
          label="Users"
          value={kpi.system.users}
          accent="var(--blue)"
        />
        <StatCard
          icon={<BarChart2 size={18} />}
          label="Changes (7d)"
          value={kpi.audit.changes_7d}
          sub={`${kpi.audit.changes_30d} in last 30d`}
          accent="var(--gray-500)"
        />
      </div>

      {/* Freshness bar */}
      <div className="card">
        <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600 }}>Data Freshness</h3>
        <div style={{ display: 'flex', gap: 0, borderRadius: 6, overflow: 'hidden', height: 20, marginBottom: 8 }}>
          <div style={{ background: 'var(--green)', width: `${verifiedPct}%`, transition: 'width 0.5s' }}
            title={`Verified: ${verifiedPct.toFixed(1)}%`} />
          <div style={{ background: 'var(--amber)', width: `${agingPct}%`, transition: 'width 0.5s' }}
            title={`Aging: ${agingPct.toFixed(1)}%`} />
          <div style={{ background: 'var(--hazard-deep)', width: `${overduePct}%`, transition: 'width 0.5s' }}
            title={`Overdue: ${overduePct.toFixed(1)}%`} />
          <div style={{ background: 'var(--red)', width: `${Math.max(0, 100 - verifiedPct - agingPct - overduePct)}%`, transition: 'width 0.5s' }}
            title="Never verified" />
        </div>
        <div style={{ display: 'flex', gap: 16, fontSize: 12, flexWrap: 'wrap' }}>
          {[
            { color: 'var(--green)', label: 'Verified',       pct: verifiedPct,      filter: 'verified' },
            { color: 'var(--amber)', label: 'Aging',           pct: agingPct,         filter: 'aging' },
            { color: 'var(--hazard-deep)', label: 'Overdue',         pct: overduePct,       filter: 'overdue' },
            { color: 'var(--red)', label: 'Never Verified',  pct: neverVerifiedPct, filter: 'never' },
          ].map(({ color, label, pct, filter }) => (
            <span
              key={label}
              onClick={() => navigate(`/directory?verification=${filter}`)}
              style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}
            >
              <span style={{ width: 10, height: 10, background: color, borderRadius: 2, display: 'inline-block' }} />
              {label} ({pct.toFixed(1)}%)
            </span>
          ))}
        </div>
      </div>

      {/* Role Coverage */}
      {kpi.role_coverage && (() => {
        const rc = kpi.role_coverage!;
        if (!rc.agencies || !rc.summary) return null;
        const displayAgencies = showAllAgencies
          ? rc.agencies
          : rc.agencies.filter((a) => a.gap_count > 0);

        return (
          <div style={{ marginBottom: 32 }}>
            {/* Section header */}
            <div style={{
              fontSize: 14, fontWeight: 600, color: 'var(--gray-500)',
              textTransform: 'uppercase', letterSpacing: '0.05em',
              marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8,
            }}>
              Role Coverage
              <span style={{
                fontSize: 12, fontWeight: 600, padding: '1px 7px',
                borderRadius: 8, background: 'var(--hazard-50)', color: 'var(--hazard-deep)',
                textTransform: 'none', letterSpacing: 0,
              }}>
                Required roles per agency
              </span>
            </div>

            {/* Summary tiles */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, marginBottom: 16 }}>
              <CoverageTile
                label="Fully Covered"
                value={rc.summary.fully_covered}
                border="var(--green)"
                onClick={() => { setShowAllAgencies(true); gapTableRef.current?.scrollIntoView({ behavior: 'smooth' }); }}
              />
              <CoverageTile
                label="Agencies with Gaps"
                value={rc.summary.agencies_with_gaps}
                border="var(--red)"
                onClick={() => { setShowAllAgencies(false); gapTableRef.current?.scrollIntoView({ behavior: 'smooth' }); }}
              />
              <CoverageTile
                label="Total Gaps"
                value={rc.summary.total_gaps}
                border="var(--amber)"
                onClick={() => { setShowAllAgencies(false); gapTableRef.current?.scrollIntoView({ behavior: 'smooth' }); }}
              />
            </div>

            {/* Gap table */}
            <div ref={gapTableRef}>
              {/* Toggle + header row */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: 12, color: 'var(--gray-500)', fontWeight: 500 }}>
                  {showAllAgencies
                    ? `All ${rc.agencies.length} agencies`
                    : `${rc.summary.agencies_with_gaps} agenc${rc.summary.agencies_with_gaps !== 1 ? 'ies' : 'y'} with gaps`}
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--gray-500)', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={showAllAgencies}
                    onChange={(e) => setShowAllAgencies(e.target.checked)}
                  />
                  Show fully covered agencies
                </label>
              </div>

              {rc.summary.agencies_with_gaps === 0 && !showAllAgencies ? (
                <div style={{
                  textAlign: 'center', padding: '32px 24px',
                  color: 'var(--gray-500)', fontSize: 14,
                  background: 'white', borderRadius: 7, border: '1px solid var(--gray-200)',
                }}>
                  ✓ All agencies have required roles filled
                </div>
              ) : (
                <div style={{ background: 'white', borderRadius: 8, border: '1px solid var(--gray-200)', overflow: 'hidden' }}>
                  {/* Table header */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 110px 110px 120px 90px',
                    padding: '6px 14px',
                    background: 'var(--gray-50)',
                    borderBottom: '1px solid var(--gray-200)',
                  }}>
                    {['Agency', 'Agency Head', 'Liaison', 'Alt. Liaison', 'Actions'].map((col) => (
                      <div key={col} style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--gray-500)' }}>
                        {col}
                      </div>
                    ))}
                  </div>

                  {/* Agency rows */}
                  {displayAgencies.map((agency) => (
                    <div
                      key={agency.agency_id}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 110px 110px 120px 90px',
                        padding: '8px 14px',
                        borderBottom: '1px solid var(--gray-100)',
                        alignItems: 'center',
                        background: agency.gap_count > 0 ? 'var(--amber-50)' : 'transparent',
                      }}
                    >
                      {/* Agency */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                        <AgencyLogo name={agency.agency_name} acronym={agency.acronym || undefined} size={20} />
                        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--nycem-blue)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {agency.agency_name}
                        </span>
                      </div>

                      {/* Agency Head */}
                      <div>
                        {agency.has_agency_head ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16, borderRadius: '50%', background: 'var(--green-50)', color: 'var(--green-700)', fontSize: 12, fontWeight: 700 }}>✓</span>
                        ) : (
                          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16, borderRadius: '50%', background: 'var(--red-50)', color: 'var(--red-700)', fontSize: 12, fontWeight: 700 }}>—</span>
                        )}
                      </div>

                      {/* Liaison */}
                      <div>
                        {agency.has_liaison ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16, borderRadius: '50%', background: 'var(--green-50)', color: 'var(--green-700)', fontSize: 12, fontWeight: 700 }}>✓</span>
                        ) : (
                          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16, borderRadius: '50%', background: 'var(--red-50)', color: 'var(--red-700)', fontSize: 12, fontWeight: 700 }}>—</span>
                        )}
                      </div>

                      {/* Alternate Liaison */}
                      <div>
                        {agency.has_alternate_liaison ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16, borderRadius: '50%', background: 'var(--green-50)', color: 'var(--green-700)', fontSize: 12, fontWeight: 700 }}>✓</span>
                        ) : (
                          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16, borderRadius: '50%', background: 'var(--red-50)', color: 'var(--red-700)', fontSize: 12, fontWeight: 700 }}>—</span>
                        )}
                      </div>

                      {/* Actions */}
                      <div>
                        {agency.gap_count > 0 && (
                          <button
                            onClick={() => {
                              // Derive the missing role(s) from the coverage booleans — an
                              // agency can lack more than one, so carry them all as a list.
                              const missing = [
                                !agency.has_agency_head       && 'Agency Head',
                                !agency.has_liaison           && 'Liaison',
                                !agency.has_alternate_liaison && 'Alternate Liaison',
                              ].filter(Boolean) as string[];
                              const roles = encodeURIComponent(missing.join(','));
                              navigate(`/update-requests?create=1&agency_id=${agency.agency_id}&roles=${roles}`);
                            }}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: 'var(--electric)', padding: 0 }}
                          >
                            Fix gaps →
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Legend */}
      <div style={{ marginTop: 32, background: 'var(--white)', border: '1px solid var(--gray-200)', borderRadius: 8, padding: '20px 24px' }}>
        <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.08em', color: 'var(--gray-500)', textTransform: 'uppercase', marginBottom: 16 }}>
          How these numbers are calculated
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 32px' }}>
          {[
            { label: 'Total Contacts',    color: 'var(--navy)',  description: 'All active contacts in the directory across all agencies. Archived and inactive contacts are excluded.' },
            { label: 'Verified',          color: 'var(--green)',      description: `Contacts whose information was confirmed accurate within the last ${agingDays} days — either by the contact themselves via the update portal, or by a Superuser.` },
            { label: 'Aging',             color: 'var(--amber)',      description: `Contacts last verified between ${agingDays} and ${overdueDays} days ago. These contacts should be prioritized for an update request.` },
            { label: 'Overdue',           color: 'var(--hazard-deep)',      description: `Contacts not verified in over ${overdueDays} days. Information may be significantly out of date. Immediate outreach recommended.` },
            { label: 'Never Verified',    color: 'var(--red)',      description: 'Contacts imported or created but never confirmed by the agency. These records should be treated as unconfirmed until verified.' },
            { label: 'Raised Flags',      color: 'var(--red)',      description: 'Contacts that have been flagged by a staff member as potentially inaccurate, unreachable, or in need of attention. Flags remain open until resolved by a Superuser.' },
            { label: 'Pending Changes',   color: 'var(--amber)',      description: 'Contact updates submitted through the update portal or HR sync that are awaiting Superuser review before being applied to the directory.' },
            { label: 'Active Lists',      color: 'var(--navy)',  description: 'Contact lists currently in use for incident response, plans, or distribution. Archived lists are excluded from this count.' },
          ].map((item) => (
            <div key={item.label} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: item.color, flexShrink: 0, marginTop: 3 }} />
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray-900)', marginBottom: 2 }}>{item.label}</div>
                <div style={{ fontSize: 12, color: 'var(--gray-500)', lineHeight: 1.5 }}>{item.description}</div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--gray-100)', fontSize: 12, color: 'var(--gray-500)', lineHeight: 1.6 }}>
          Verification percentages are calculated against total active contacts. A contact can only be in one verification category at a time. Last updated: real-time on each page load.
        </div>
      </div>
    </div>
  );
};

export default KPIDashboardPage;
